export type WorkflowTrigger = TimeBasedTrigger | EventBasedTrigger;
export type MessageContent = {
  subject: string;
  body: string;
};
export type MessageStatus = string;
export type ChannelType = string;
export type RecipientType = string;

export interface TimeDelay {
  unit: string;
  value: number;
}

export interface WorkflowTriggerBase {
  type: string;
}

export interface TimeBasedTrigger extends WorkflowTriggerBase {
  type: "time-based";
  timeType: string;
  date?: string;
  time?: string;
  relativeEvent?: string;
  timeDelay?: TimeDelay;
  beforeAfter?: string;
}
export interface EventBasedTrigger extends WorkflowTriggerBase {
  type: "event-based";
  eventType: string;
  conditions?: {
    field: string;
    operator: string;
    value: any;
  }[];
}

export interface WorkflowAction {
  channel: string;
  source: string;
  content?: string; // For inline messages
  templateId?: number; // For template-based messages
  timeDelay?: TimeDelay; // Delay before this action
  subject: string;
  channelType: string;
  sequenceOrder: number;
  delay?: {
    value: number;
    unit: string;
  } | null;
  messageSource: {
    type: string;
    templateId?: number | null;
    content?: {
      subject?: string;
      body?: string;
    } | null;
  };
  channelSpecificConfig: {
    cc?: string[];
    bcc?: string[];
  };
}

export interface WorkflowMetadata {
  [key: string]: any;
}

export interface Workflow {
  id: number;
  name: string;
  description?: string;
  remoteUtilityId: number;
  recipientType: string;
  recipientTypeDisplay: string;
  recipientList?: number | null;
  eventType?: string | null;
  startDate?: string;
  triggerType: TriggerType;
  triggerTypeDisplay: string;
  isDraft: boolean;
  isActive: boolean;
  actions: WorkflowAction[];
  metadata: WorkflowMetadata;
  totalNotifications: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
  successRate: number;
  createdDate: string;
  lastModifiedDate: string;
  createdUserRemoteName: string;
  lastModifiedUserRemoteName: string;
}

export interface WorkflowRun {
  id: number;
  successRate: number;
  remoteUtilityId: number;
  workflow?: number | null;
  recipientList?: number | null;
  status: number;
  statusDisplay: string;
  template?: any | null;
  recipientsCount: number;
  isActive: boolean;
  startDate: string;
  completeDate?: string;
  startedAt: string;
  completedAt?: string;
  typeOf: string;
}

export interface NotificationDetail {
  id: number;
  remoteUtilityId: number;
  channelType: string;
  message: {
    body: string;
    subject?: string;
    templateId?: number | null;
    to: string | string[];
    cc?: string[];
    bcc?: string[];
    channel: string;
  };
  runTime: string;
  statusDisplay: string;
  template?: any;
  workflow?: number | null;
  recipientList?: number | null;
  eventType?: string | null;
  eventData?: any;
  entityId?: number | null;
  actionType?: string | null;
  status: number;
  run?: WorkflowRun | null;
  metadata: {
    channel: string;
    response?: any;
    singleSend?: boolean;
    error?: string;
    success?: boolean;
    failedAt?: string;
  };
  recipientData: {
    to: string | string[];
    cc?: string[];
    bcc?: string[];
    channel: string;
    entityId?: number;
    actionType?: string;
    id?: number;
    email?: string;
    consName?: string;
    contactNo?: string;
    consumerNo?: string;
    consEmailId?: string;
    consumerName?: string;
    accountNumber?: string;
    consLastName?: string;
    consumerEmail?: string;
    consContactNo?: string;
    consFirstName?: string;
  };
  createdDate: string;
  lastModifiedDate: string;
  createdUserRemoteName?: string | null;
  lastModifiedUserRemoteName?: string | null;
}

export interface WorkflowApiResponse {
  pageNumber: number;
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Workflow[];
}

export interface WorkflowRunApiResponse {
  pageNumber: number; // Changed from pageNumber
  count: number;
  next?: string | null;
  previous?: string | null;
  results: WorkflowRun[];
  // Add these fields if they exist in the single run response:
  successRate?: number;
  recipientsCount?: number;
  typeOf?: string;
}

export interface NotificationApiResponse {
  pageNumber: number;
  count: number;
  next?: string | null;
  previous?: string | null;
  results: NotificationDetail[];
}

export interface CreateWorkflowRequest {
  remoteUtilityId: string;
  name: string;
  description?: string;
}

export interface UpdateWorkflowTriggerRequest {
  triggerType: 0 | 1;
  startDate?: string;
  eventType?: string;
  recipientType?: string;
  recipientList?: number | null;
}

export interface UpdateWorkflowActionsRequest {
  processActions: boolean;
  isDraft?: boolean;
  actions?: WorkflowAction[];
}

export interface CloneWorkflowRequest {}

export interface CreateWorkflowResponse {
  result: Workflow;
}

export interface UpdateWorkflowResponse extends Workflow {}

export interface CloneWorkflowResponse {
  result: Workflow;
}

export interface WorkflowsManagerProps {
  statusFilter?: string;
}
export interface WorkflowsManagerFilterProps {
  statusFilter?: string;
  workflows?: WorkflowApiResponse;
  loading: boolean;
}
export interface MessageTemplate {
  id: number;
  template_name: string;
  content: {
    body: string;
    subject?: string;
    body_len: number;
    subject_len?: number;
  };
  template_alias: string | null;
}

export interface WorkflowFilters {
  remoteUtilityId?: string;
  isActive?: boolean;
  isDraft?: boolean;
  triggerType?: string;
  recipientType?: RecipientType;
  page: number;
  limit: number;
  searchData?: string;
  status?: string[];
}

export interface WorkflowRunFilters {
  searchData?: string;
  remoteUtilityId?: string;
  workflow?: number;
  statusDisplay?: string;
  page: number;
  limit: number;
}

export interface NotificationFilters {
  remoteUtilityId?: number;
  runId?: number;
  workflow?: number;
  status?: number;
  channelType?: ChannelType;
  page?: number;
  limit?: number;
}

export interface HistoryMessage {
  id: string;
  subject: string;
  recipient: string;
  timestamp: Date;
  status: MessageStatus;
  type: string;
  content: string;
  tags?: string[];
}
export enum TriggerType {
  EVENT_BASED = 0,
  TIME_BASED = 1,
}

export enum WorkflowStatus {
  DRAFT = 0,
  ACTIVE = 1,
  PAUSED = 2,
}
export interface WorkflowDetails {
  name: string;
  description: string;
  audienceType: string;
  audienceListId?: string;
  workflowId?: number;
  timestamp?: string;
}

export interface WorkflowTriggerData {
  triggerType: string;
  timeType?: string;
  date?: string;
  time?: string;
  time24h?: string;
  relativeEvent?: string;
  relativeAmount?: number;
  relativeUnit?: string;
  eventType?: string;
  timestamp?: string;
}

export interface WorkflowActionsData {
  actions: WorkflowAction[];
  timestamp?: string;
}

export interface WorkflowFormData {
  workflowId?: number;
  details?: WorkflowDetails;
  trigger?: WorkflowTriggerData;
  actions?: WorkflowActionsData;
  review?: any;
}

export const DEFAULT_WORKFLOW_FORM_DATA: WorkflowFormData = {
  details: {
    name: "",
    description: "",
    audienceType: "CONSUMER",
    audienceListId: "",
  },
  trigger: {
    triggerType: "time-based",
    timeType: "specific-date",
    date: "",
    time: "",
    eventType: "nsc-created",
  },
  actions: {
    actions: [],
  },
  review: {},
};

export interface CreateWorkflowResponse {
  id: number;
  name: string;
  description: string;
  recipientType: string;
  recipientList?: number;
  status: string;
  createdDate: string;
}

export interface UpdateTriggerRequest {
  triggerType: number; // 0 = event-based, 1 = time-based
  startDate?: string; // ISO string for time-based
  eventType?: string; // for event-based
}
